// src/components/MastersCrudPage.jsx
import { useEffect, useState } from "react";
import PropTypes from "prop-types";

export default function MastersCrudPage({
  title,
  fetchList,
  createItem,
  updateItem,
  deleteItem,
  columns, // [{ key, label }]
  renderForm, // ({ form, setForm, mode }) => jsx
  itemLabel = (it) => it.id || "(item)",
  initialForm = () => ({}),
  modalPosition = "right", // "center" | "right" | "left"
}) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("create"); // create|edit
  const [form, setForm] = useState(initialForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setErr("");
    setLoading(true);
    try {
      const data = await fetchList();

      // Normalizar la respuesta: data puede ser un array, o un objeto con { data: [...] }, etc.
      if (Array.isArray(data)) {
        setItems(data);
      } else if (data && Array.isArray(data.data)) {
        setItems(data.data);
      } else if (data && Array.isArray(data.resources)) {
        // por si la API devuelve { resources: [...] }
        setItems(data.resources);
      } else if (data && Array.isArray(data.locations)) {
        // por si la API devuelve { locations: [...] }
        setItems(data.locations);
      } else {
        setItems([]); // fallback seguro
      }

      // Si quieres depurar, descomenta:
      // console.log("fetchList raw response:", data);
    } catch (e) {
      setErr(e.response?.data?.error || e.message || "Error al cargar");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setMode("create");
    setForm(initialForm());
    setEditingId(null);
    setOpen(true);
  };

  const openEdit = (it) => {
    setMode("edit");
    setForm({ ...it });
    setEditingId(it.id);
    setOpen(true);
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      if (mode === "create") {
        await createItem(form);
      } else {
        await updateItem(editingId, form);
      }
      await load();
      setOpen(false);
    } catch (err) {
      setErr(err.response?.data?.error || err.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar?")) return;
    try {
      await deleteItem(id);
      await load();
    } catch (err) {
      setErr(err.response?.data?.error || err.message || "Error al eliminar");
    }
  };

  // panel position classes (container + inner panel sizing/transform)
  const panelContainerClass = {
    center: "fixed inset-0 flex items-center justify-center",
    right: "fixed inset-0 flex items-stretch justify-end",
    left: "fixed inset-0 flex items-stretch justify-start",
  }[modalPosition];

  const panelWidthClass = modalPosition === "center" ? "w-full max-w-2xl" : "w-full sm:w-96 md:w-[420px]";

  // translate classes for initial hidden state (used with conditional classes)
  const hiddenTransformClass =
    modalPosition === "center" ? "translate-y-4 opacity-0" : modalPosition === "right" ? "translate-x-full" : "-translate-x-full";

  return (
    <div className="appointments-module p-4 space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold">{title}</h2>
        <div className="ml-auto flex gap-2">
          <button onClick={openCreate} className="btn">Nuevo</button>
          <button onClick={load} className="btn btn-muted">Refrescar</button>
        </div>
      </div>

      {err && <div className="text-sm text-red-600 p-2 border rounded">{err}</div>}

      <div className="overflow-auto border rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-800 text-slate-100">
            <tr>
              {columns.map(c => <th key={c.key} className="p-2 text-left border-b">{c.label}</th>)}
              <th className="p-2 border-b">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns.length + 1} className="p-4">Cargando...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={columns.length + 1} className="p-4 text-gray-500">Sin datos</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} className="odd:bg-slate-900 even:bg-slate-950">
                {columns.map(c => <td key={c.key} className="p-2 align-top">{String(it[c.key] ?? "")}</td>)}
                <td className="p-2">
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(it)} className="px-2 py-1 text-xs rounded bg-blue-600 text-white">Editar</button>
                    <button onClick={() => handleDelete(it.id)} className="px-2 py-1 text-xs rounded bg-red-700 text-white">Eliminar</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50">
          {/* overlay */}
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />

          {/* container positions panel to center / right / left */}
          <div className={`${panelContainerClass} z-50 pointer-events-none`}>
            <div
              // panel: pointer-events-auto so it receives clicks, transform for animation
              className={`pointer-events-auto ${panelWidthClass} ${modalPosition === "center" ? "sm:rounded-2xl rounded-lg" : "h-full sm:h-auto"} bg-[#020817] border ${modalPosition === "center" ? "border-gray-700" : "border-l border-gray-700"} transform transition-transform duration-200 ease-out ${open ? "translate-x-0 translate-y-0 opacity-100" : hiddenTransformClass}`}
              role="dialog"
              aria-modal="true"
            >
              <div className={`p-4 ${modalPosition === "center" ? "" : "h-full overflow-auto"}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold">{mode === "create" ? "Nuevo" : "Editar"} {title}</h3>
                  <button onClick={() => setOpen(false)} className="text-xs text-gray-400">✕</button>
                </div>

                <form onSubmit={handleSave} className="space-y-3">
                  {renderForm({ form, setForm, mode })}

                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setOpen(false)} className="px-3 py-1 text-sm rounded border">Cancelar</button>
                    <button type="submit" disabled={saving} className="px-4 py-1 rounded bg-[color:var(--accent,#3b82f6)] text-white">
                      {saving ? "Guardando..." : "Guardar"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

MastersCrudPage.propTypes = {
  title: PropTypes.string.isRequired,
  fetchList: PropTypes.func.isRequired,
  createItem: PropTypes.func.isRequired,
  updateItem: PropTypes.func.isRequired,
  deleteItem: PropTypes.func.isRequired,
  columns: PropTypes.array.isRequired,
  renderForm: PropTypes.func.isRequired,
  initialForm: PropTypes.func,
  modalPosition: PropTypes.oneOf(["center", "right", "left"]),
};
